import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";

// Local components
import Card from "../Card/Card";
import CardHeader from "../Card/CardHeader.js";
import CardBody from "../Card/CardBody.js";
import GridItem from "../Grid/GridItem.js";
import GridContainer from "../Grid/GridContainer.js";

// Hooks
import { useQueryData } from "../../hooks/useQueryData";

// Utils
import { defaultPageSize } from "../../common/DataGridColumns";
import { removeEmptyValues } from "../../utils/shallowObjectsCompare";

export default function CustomDataGrid2(props) {
    const {
        title,
        rows,
        columns,
        totalRows,
        loading,
        handleRowClick,
        handleFilterChange,
        handlePageChange,
        handleDataInit,
        initPageSize,
        ignoreUrl,
        getCellClassName,
        headerChildren,
        reset,
    } = props;

    const { queryData, updateQuery } = useQueryData();

    const [fixedQuery, setFixedQuery] = useState({});
    const [rowsPerPage, setRowsPerPage] = useState(initPageSize || defaultPageSize);
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState({});
    const [timer, setTimer] = useState(null);


    useEffect(() => {
        setFilters({ offset: 0, limit: 25 });
        handleFilterChange({ offset: 0, limit: 25 });
        tryToUpdateUrlQuery({ offset: 0, limit: 25 });
    }, [reset]);


    const onPaginationModelChange = (newPage, newRowsPerPage) => {
        let neededPage = newPage;

        if (rowsPerPage !== newRowsPerPage) {
            neededPage = 0;
            setPage(0);
            setRowsPerPage(newRowsPerPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        const payload = {
            ...queryData,
            ...filters,
            offset: neededPage * newRowsPerPage,
            limit: newRowsPerPage
        };


        if (rowsPerPage === newRowsPerPage) {
            setPage(newPage);
            setRowsPerPage(newRowsPerPage);
        };

        handlePageChange(payload);
        tryToUpdateUrlQuery(payload);
    };


    const onSortModelChange = (changedColumn, direction) => {
        const sortData = { field: [changedColumn], sort: direction };
        const payload = { ...queryData, ...sortData, offset: 0, limit: rowsPerPage };

        handleFilterChange(payload);
        tryToUpdateUrlQuery(payload);
    };

    //OLD params: changedColumn, filterList, type, changedColumnIndex;
    const onFilterModelChange = (model) => {
        const incomingItem = model.items[0];

        if (!incomingItem || !incomingItem.value) {
            setFilters(null);
            handleFilterChange({ ...fixedQuery, offset: 0, limit: rowsPerPage });
            tryToUpdateUrlQuery({ offset: 0, limit: rowsPerPage });
            return;
        };

        const filter = {
            [incomingItem.field]: String(incomingItem.value)
        };

        setFilters(filter);

        const payload = {
            ...filter,
            offset: 0,
            limit: rowsPerPage
        };

        clearTimeout(timer);

        const newTimer = setTimeout(() => {
            handleFilterChange(payload);
            tryToUpdateUrlQuery(payload);
        }, 500);

        setTimer(newTimer);

        //MUI V5 DataGrid free option allows filtering by one criterium only
        //We should discuss what should we do here

        //OLD CODE: 
        // if (type === "chip" && changedColumn) {
        //     if (filterList[changedColumn]?.length && !filterList[changedColumnIndex].filter((x) => !!x)?.length) {
        //         filterList[changedColumnIndex] = [];
        //     };

        //     let transformedFilters = { ...queryData, ...filters };

        //     if (transformedFilters[changedColumn]) delete transformedFilters[changedColumn];
        //     setFilters({ ...transformedFilters });

        //     handleFilterChange({ ...transformedFilters, offset: 0, limit: rowsPerPage });
        //     tryToUpdateUrlQuery({ ...transformedFilters, offset: 0, limit: rowsPerPage });

        // } else if (type === "reset") {
        //     setFilters(null);
        //     handleFilterChange({ ...fixedQuery, offset: 0, limit: rowsPerPage });
        //     tryToUpdateUrlQuery({ offset: 0, limit: rowsPerPage });

        // } else if (changedColumn) {
        //     let changedData = filterList[changedColumnIndex].filter((x) => !!x).length ? filterList[changedColumnIndex] : [];
        //     setFilters({ ...filters, [changedColumn]: changedData.toString() });

        //     const payload = {
        //         ...filters,
        //         ...queryData,
        //         [changedColumn]: changedData.toString(),
        //         offset: 0,
        //         limit: rowsPerPage,
        //     };

        //     if (!changedData || changedData.length < 1) delete payload[changedColumn];
        //     const filteredPayload = removeEmptyValues(payload);

        //     clearTimeout(timer);

        //     const newTimer = setTimeout(() => {
        //         handleFilterChange(filteredPayload);
        //         tryToUpdateUrlQuery(filteredPayload);
        //     }, 500);

        //     setTimer(newTimer);
        // };
    };

    const tryToUpdateUrlQuery = (payload) => {
        if (!ignoreUrl) {
            updateQuery({
                ...fixedQuery,
                ...payload
            });
        };
    };

    return (
        <Card>
            <CardHeader color="rose">
                <Typography variant="h6">
                    {title}
                    {loading && <CircularProgress color="inherit" size={24} sx={{ ml: 2, position: "relative", top: 4, zIndex: 9 }} />}
                    {headerChildren}
                </Typography>
            </CardHeader>
            <CardBody>
                <GridContainer>
                    <GridItem xs={12} sm={12} md={12}>
                        <Box className="data-grid no-border" textAlign="right">
                            {rows && (
                                <>
                                    <DataGrid
                                        page={page}
                                        pageSize={rowsPerPage}
                                        rows={rows}
                                        rowSelection={false}
                                        columns={columns}
                                        disableRowSelectionOnClick={true}
                                        getRowId={(row) => row.id}
                                        pagination
                                        pageSizeOptions={[25, 50, 100]}
                                        rowCount={totalRows}
                                        paginationMode="server"
                                        sortingMode="server"
                                        filterMode="server"
                                        loading={loading}
                                        disableColumnMenu={true}
                                        slots={{ toolbar: GridToolbar }}
                                        slotProps={{
                                        }}
                                        onRowClick={({ id }) => handleRowClick(id)}
                                        onPaginationModelChange={(params) => {
                                            const { page, pageSize } = params;
                                            onPaginationModelChange(page, pageSize);
                                        }}
                                        onSortModelChange={(model) =>
                                            onSortModelChange(model[0]?.field || '', model[0]?.sort || '')
                                        }
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 25 } },
                                        }}
                                        onFilterModelChange={(model) => {
                                            onFilterModelChange(model);
                                        }}
                                    />
                                </>
                            )}
                        </Box>
                    </GridItem>
                </GridContainer>
            </CardBody>
        </Card>
    );
}